import Auth from '@aws-amplify/auth'
import Amplify from '@aws-amplify/core'
import configProvider from '../../config'
import { JWTIssue } from '../../models/auth'
import { JWTFromAuth0 } from '../data-provider/auth'

export interface CognitoUser {
  username: string
  userId: string
  signInDetails?: any
}

export class CognitoAuthService {
  private isConfigured = false

  constructor() {
    // Only configure Cognito if it's the active auth provider
    if (configProvider.config.authProvider === 'cognito') {
      this.configureCognito()
    }
  }

  private configureCognito() {
    if (this.isConfigured) return

    const cognitoConfig = configProvider.config.cognito

    if (!cognitoConfig.userPoolId || !cognitoConfig.userPoolWebClientId) {
      console.log('Cognito configuration is missing required parameters')
      return
    }

    const amplifyConfig = {
      Auth: {
        region: cognitoConfig.region,
        userPoolId: cognitoConfig.userPoolId,
        userPoolWebClientId: cognitoConfig.userPoolWebClientId,
        mandatorySignIn: true,
        ...(cognitoConfig.domain && {
          oauth: {
            domain: cognitoConfig.domain,
            scope: cognitoConfig.scopes,
            redirectSignIn: cognitoConfig.redirectSignIn,
            redirectSignOut: cognitoConfig.redirectSignOut,
            responseType: cognitoConfig.responseType,
          },
        }),
        responseType: 'code',
      },
    }

    Amplify.configure(amplifyConfig)

    this.isConfigured = true
  }

  async signInUser(username: string, password: string): Promise<any> {
    try {
      console.log('Attempting Cognito sign in', { username })
      const result = await Auth.signIn(username, password)
      console.log('Cognito sign in successful', { username })
      return result
    } catch (error) {
      console.log('Cognito sign in failed', { username, error })
      throw error
    }
  }

  async signInWithHostedUI(): Promise<void> {
    try {
      console.log('Initiating Cognito hosted UI sign in')
      await Auth.federatedSignIn()
    } catch (error) {
      console.log('Cognito hosted UI sign in failed', { error })
      throw error
    }
  }

  async signOutFromHostedUI(): Promise<void> {
    try {
      const cognitoConfig = configProvider.config.cognito

      if (!cognitoConfig.domain || !cognitoConfig.userPoolWebClientId) {
        throw new Error('Cognito configuration missing domain or client ID')
      }

      // Build the logout URL for hosted UI
      const logoutUrl = `https://${cognitoConfig.domain}/logout?client_id=${
        cognitoConfig.userPoolWebClientId
      }&logout_uri=${encodeURIComponent(window.location.origin + '/auth/cognito/logout')}`

      window.location.assign(logoutUrl)
    } catch (error) {
      console.log('Cognito hosted UI sign out failed', { error })
      throw error
    }
  }

  async getCurrentAuthenticatedUser(): Promise<CognitoUser | null> {
    try {
      const user = await Auth.currentAuthenticatedUser()
      console.log('Current Cognito user retrieved', { username: user.username })
      return user
    } catch (error) {
      console.log('No current Cognito user', { error })
      return null
    }
  }

  async getCurrentAuthSession(): Promise<any> {
    try {
      const session = await Auth.currentSession()
      console.log('Current Cognito session retrieved')
      return session
    } catch (error) {
      console.log('No current Cognito session', { error })
      throw error
    }
  }

  async getIdToken(): Promise<string> {
    try {
      const session = await Auth.currentSession()
      const idToken = session.getIdToken().getJwtToken()
      console.log('Cognito ID token retrieved')
      return idToken
    } catch (error) {
      console.log('Failed to get Cognito ID token', { error })
      throw error
    }
  }

  async refreshAuthSession(): Promise<any> {
    try {
      console.log('Refreshing Cognito session')
      const user = await Auth.currentAuthenticatedUser()
      const session = await Auth.currentSession()

      if (session.isValid()) {
        console.log('Cognito session is still valid')
        return session
      }

      // Force refresh
      const refreshedSession = await user.refreshSession(session.getRefreshToken())
      return refreshedSession
    } catch (error) {
      console.log('Failed to refresh Cognito session', { error })
      throw error
    }
  }

  async handleAuthCallback(): Promise<JWTIssue> {
    try {
      // For v5, the callback is handled automatically
      const session = await Auth.currentSession()
      const idToken = session.getIdToken().getJwtToken()
      return await JWTFromAuth0(idToken)
    } catch (error) {
      console.log('Failed to handle Cognito auth callback', { error })
      throw error
    }
  }
}

export const cognitoAuth = new CognitoAuthService()
